import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../context/WalletContext";
import { Wallet, Plus, ArrowDownLeft, ArrowUpRight, Lock, CheckCircle2, Loader2 } from "lucide-react";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import PinModal from "../components/PinModal";

const QUICK_AMOUNTS = [1000, 10000, 50000, 100000, 250000, 500000];

const TX_LABELS = {
  topup: { label: "Top-up Saldo", icon: ArrowDownLeft, color: "text-[#00FF66]" },
  bid_lock: { label: "Bid Lock", icon: Lock, color: "text-[#7C6EFF]" },
  bid_refund: { label: "Refund Bid", icon: ArrowDownLeft, color: "text-[#00F0FF]" },
  bid_settle: { label: "Auction Won", icon: ArrowUpRight, color: "text-[#FFD700]" },
  auction_payout: { label: "Payout (Seller)", icon: ArrowDownLeft, color: "text-[#00FF66]" },
  admin_credit: { label: "Saldo dari Admin", icon: ArrowDownLeft, color: "text-[#00FF66]" },
  admin_adjust: { label: "Penyesuaian Saldo (Admin)", icon: ArrowUpRight, color: "text-[#FFD700]" },
  purchase: { label: "Pembelian Produk", icon: ArrowUpRight, color: "text-[#FF003C]" },
  refund_dispute: { label: "Refund Komplain", icon: ArrowDownLeft, color: "text-[#00F0FF]" },
  referral_bonus: { label: "Bonus Referral", icon: ArrowDownLeft, color: "text-[#00FF66]" },
  referral_welcome_bonus: { label: "Bonus Member Baru", icon: ArrowDownLeft, color: "text-[#00FF66]" },
  transfer_seller_to_buyer: { label: "Transfer dari Saldo Seller", icon: ArrowDownLeft, color: "text-[#00F0FF]" },
};


export default function WalletPage() {
  const { user, loading } = useAuth();
  const wallet = useWallet();
  const [txs, setTxs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(100000);
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const topupRef = searchParams.get("topup_ref");
  const [topupInfo, setTopupInfo] = useState(null);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/wallet/transactions").then((r) => setTxs(r.data));
    api.get("/payment-channels").then((r) => {
      setChannels(r.data);
      if (r.data.length > 0) setChannel(r.data[0].code);
    });
  }, [user]);

  // Poll topup status when returning from Tripay
  useEffect(() => {
    if (!topupRef) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/wallet/topup/status/${topupRef}`);
        setTopupInfo(data);
        if (data.status === "paid") {
          toast.success(`Saldo Rp ${data.amount.toLocaleString("id-ID")} masuk!`);
          wallet.refresh();
          api.get("/wallet/transactions").then((r) => setTxs(r.data));
          return;
        }
        if (data.status === "expired" || data.status === "failed") return;
        if (!cancelled) setTimeout(poll, 3000);
      } catch {
        if (!cancelled) setTimeout(poll, 3000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [topupRef]);

  const [pinMode, setPinMode] = useState(null); // null | "verify" | "set"

  if (loading) return <div className="text-white/50 text-center py-32">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;

  const submitTopup = async (pin) => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/topup", {
        amount: parseInt(amount),
        payment_method: channel,
        origin_url: window.location.origin,
        customer_phone: phone || "081234567890",
        pin,
      });
      window.location.href = data.checkout_url || data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Topup failed");
      setBusy(false);
    }
  };

  const topup = async (e) => {
    e?.preventDefault();
    if (!channel) { toast.error("Pilih channel pembayaran"); return; }
    if (amount < 1000) { toast.error("Minimal Rp 1.000"); return; }
    // Need PIN — open modal (set if user has no PIN, verify if has)
    setPinMode(user?.has_pin ? "verify" : "set");
  };

  return (
    <div data-testid="wallet-page" className="max-w-5xl mx-auto px-4 md:px-8 py-12">
      <span className="tag tag-accent">// SALDO USTORE</span>
      <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mt-3 mb-2">Wallet</h1>
      <p className="text-white/55 mb-8">Saldo Ustore dipakai untuk bid auction & belanja produk.</p>

      {topupInfo && topupInfo.status !== "paid" && (
        <div className="mb-6 border border-[#FFD700]/40 bg-[#FFD700]/5 p-4 flex items-center gap-3" data-testid="topup-pending-banner">
          <Loader2 className="w-5 h-5 text-[#FFD700] animate-spin" />
          <div className="flex-1">
            <p className="text-[#FFD700] font-bold">Menunggu konfirmasi pembayaran...</p>
            <p className="text-white/60 text-sm">{topupRef} · {formatIDR(topupInfo.amount)}</p>
          </div>
          {topupInfo.checkout_url && (
            <a href={topupInfo.checkout_url} className="px-3 py-2 bg-[#FFD700] text-black text-xs uppercase font-bold tracking-widest">
              Lanjut Bayar
            </a>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-6" data-testid="wallet-balance-card">
          <Wallet className="w-5 h-5 text-[#00F0FF] mb-3" />
          <div className="font-display font-black text-3xl text-[#00F0FF]">{formatIDR(wallet.balance || 0)}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Saldo Buyer</div>
          <div className="text-[10px] text-white/40 mt-1">Untuk checkout produk</div>
        </div>
        {user?.role === "seller" && (
          <div className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-6" data-testid="wallet-seller-card">
            <Wallet className="w-5 h-5 text-[#00FF66] mb-3" />
            <div className="font-display font-black text-3xl text-[#00FF66]">{formatIDR(wallet.seller_balance || 0)}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Saldo Seller (Pendapatan)</div>
            <div className="text-[10px] text-white/40 mt-1">Tarik di Seller Dashboard</div>
          </div>
        )}
        <div className="border border-white/10 p-6" data-testid="wallet-locked-card">
          <Lock className="w-5 h-5 text-[#7C6EFF] mb-3" />
          <div className="font-display font-black text-3xl text-white">{formatIDR(wallet.locked_balance || 0)}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Saldo Terkunci</div>
          <div className="text-[10px] text-white/40 mt-1">Bid aktif / escrow pending</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        {/* Topup */}
        <div className="border border-white/10 p-6">
          <h2 className="font-display font-bold text-2xl text-white flex items-center gap-2 mb-4">
            <Plus className="w-5 h-5 text-[#00F0FF]" /> Top-up Saldo
          </h2>
          <form onSubmit={topup} className="space-y-4" data-testid="topup-form">
            <div className="grid grid-cols-3 gap-2">
              {QUICK_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  data-testid={`quick-amount-${amt}`}
                  onClick={() => setAmount(amt)}
                  className={`p-3 border text-sm font-bold ${amount === amt ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/70 hover:border-white/30"}`}
                >
                  {formatIDR(amt, { compact: true })}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/60">Jumlah (Rp)</label>
              <input
                data-testid="topup-amount-input"
                type="number"
                step="1000"
                min="1000"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white text-lg px-4 py-3 mt-2"
              />
              <p className="text-[10px] text-white/40 mt-1">{formatIDR(amount || 0)} · Min Rp 1.000</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/60">Channel</label>
              <select
                data-testid="topup-channel-select"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white px-4 py-3 mt-2"
              >
                {channels.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/60">No. HP (untuk e-wallet)</label>
              <input
                data-testid="topup-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="081234567890"
                className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm px-4 py-2.5 mt-2"
              />
            </div>
            <button
              data-testid="topup-submit-btn"
              type="submit"
              disabled={busy || channels.length === 0}
              className="btn-primary w-full disabled:opacity-50"
            >
              {busy ? "Memproses..." : `Top-up ${formatIDR(amount || 0)}`}
            </button>
            <p className="text-[10px] uppercase tracking-widest text-white/30 text-center">
              Dipotong fee Tripay sesuai channel · 100% manual ke saldo setelah bayar
            </p>
          </form>
        </div>

        {/* Transactions */}
        <div className="border border-white/10 p-6 h-fit">
          <h3 className="font-display font-bold text-xl text-white mb-4">Riwayat Transaksi</h3>
          {txs.length === 0 ? (
            <p className="text-white/50 text-sm" data-testid="no-tx">Belum ada transaksi.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="tx-list">
              {txs.map((t) => {
                const meta = TX_LABELS[t.type] || { label: t.type, icon: CheckCircle2, color: "text-white" };
                const Icon = meta.icon;
                const amount = parseFloat(t.amount);
                return (
                  <div key={t.id} className="flex items-center gap-3 border-b border-white/5 pb-2">
                    <Icon className={`w-4 h-4 shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm">{meta.label}</p>
                      <p className="text-[10px] text-white/40">{new Date(t.created_at).toLocaleString("id-ID")}</p>
                    </div>
                    <p className={`font-bold text-sm ${amount >= 0 ? "text-[#00FF66]" : "text-[#FF003C]"}`}>
                      {amount >= 0 ? "+" : ""}
                      {formatIDR(amount)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {pinMode && (
        <PinModal
          mode={pinMode}
          isChange={false}
          title={pinMode === "set" ? "Setel PIN Transaksi" : "Konfirmasi Topup"}
          description={pinMode === "set" ? "Buat PIN 6 digit untuk setiap transaksi" : `Top-up ${formatIDR(amount)} via ${channel}`}
          onClose={() => setPinMode(null)}
          onSuccess={(pin) => submitTopup(pin)}
        />
      )}
    </div>
  );
}
